import React, { useEffect } from 'react';
import { X } from 'lucide-react';

interface KeyboardShortcutsOverlayProps {
  open: boolean;
  onClose: () => void;
}

interface ShortcutItem {
  keys: string[];
  description: string;
}

interface ShortcutSection {
  title: string;
  shortcuts: ShortcutItem[];
}

const SECTIONS: ShortcutSection[] = [
  {
    title: 'Playback',
    shortcuts: [
      { keys: ['Space'], description: 'Play / Pause' },
      { keys: ['←', '→'], description: '±5 seconds' },
      { keys: ['Shift', '←', '→'], description: '±1 second' },
      { keys: ['1'], description: '0.5× speed' },
      { keys: ['2'], description: '1× speed' },
      { keys: ['3'], description: '1.25× speed' },
      { keys: ['4'], description: '1.5× speed' },
    ],
  },
  {
    title: 'Navigation',
    shortcuts: [
      { keys: ['↑', '↓'], description: 'Previous / Next utterance' },
      { keys: ['Esc'], description: 'Deselect utterance' },
    ],
  },
  {
    title: 'Emotion (Alt + number)',
    shortcuts: [
      { keys: ['Alt', '1'], description: 'Neutral' },
      { keys: ['Alt', '2'], description: 'Angry' },
      { keys: ['Alt', '3'], description: 'Sad' },
      { keys: ['Alt', '4'], description: 'Happy' },
      { keys: ['Alt', '5'], description: 'Fearful' },
      { keys: ['Alt', '6'], description: 'Sarcastic' },
      { keys: ['Alt', '7'], description: 'Frustrated' },
      { keys: ['Alt', '8'], description: 'Hesitant' },
      { keys: ['Alt', '9'], description: 'Confident' },
    ],
  },
  {
    title: 'Sentiment',
    shortcuts: [
      { keys: ['Shift', 'P'], description: 'Positive' },
      { keys: ['Shift', 'N'], description: 'Negative' },
      { keys: ['Shift', 'U'], description: 'Neutral' },
      { keys: ['Shift', 'M'], description: 'Mixed' },
    ],
  },
  {
    title: 'Speech Markers (Ctrl + number)',
    shortcuts: [
      { keys: ['Ctrl', '1'], description: 'Pause' },
      { keys: ['Ctrl', '2'], description: 'Long Pause' },
      { keys: ['Ctrl', '3'], description: 'Hesitation' },
      { keys: ['Ctrl', '4'], description: 'Laughter' },
      { keys: ['Ctrl', '5'], description: 'Sigh' },
      { keys: ['Ctrl', '6'], description: 'Interruption' },
      { keys: ['Ctrl', '7'], description: 'Overlap' },
      { keys: ['Ctrl', '8'], description: 'Emphasis' },
    ],
  },
  {
    title: 'Actions',
    shortcuts: [
      { keys: ['Ctrl', 'E'], description: 'Entity modal' },
      { keys: ['Ctrl', 'S'], description: 'Save' },
      { keys: ['Ctrl', 'Enter'], description: 'Submit' },
      { keys: ['?'], description: 'Show help' },
    ],
  },
];

const KeyBadge: React.FC<{ label: string }> = ({ label }) => (
  <kbd className="inline-flex items-center px-1.5 py-0.5 text-xs font-mono font-semibold bg-gray-100 text-gray-700 border border-gray-300 rounded shadow-sm">
    {label}
  </kbd>
);

const KeyboardShortcutsOverlay: React.FC<KeyboardShortcutsOverlayProps> = ({ open, onClose }) => {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Keyboard shortcuts"
    >
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto m-4">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Keyboard Shortcuts</h2>
            <p className="text-xs text-gray-500 mt-0.5">Use these shortcuts to annotate faster</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content grid */}
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          {SECTIONS.map((section) => (
            <div key={section.title}>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 border-b border-gray-100 pb-1">
                {section.title}
              </h3>
              <div className="space-y-2">
                {section.shortcuts.map((shortcut, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">{shortcut.description}</span>
                    <div className="flex items-center gap-1">
                      {shortcut.keys.map((key, ki) => (
                        <React.Fragment key={ki}>
                          {ki > 0 && (
                            <span className="text-xs text-gray-400">+</span>
                          )}
                          <KeyBadge label={key} />
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default KeyboardShortcutsOverlay;
